import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Accordion, Input, Text, StatusChip } from "@fragments";
import { ReactComponent as GinasteeCard } from "@assets/draws/card-ginastee.svg";
import * as Mui from "@mui/material";
import {
    IconInfoCircle,
    IconSquareRoundedMinus,
    IconChecks,
    IconRefresh,
    IconAlertTriangle,
} from "@tabler/icons-react";
import Parser from "@helpers/parser";
import DocumentRow from "./components/DocumentRow";
import "./styles.scss";

// API status → chip copy, resolved from the caller's `t`. The keys on the left
// and the `variant`/`Icon` are API and presentation data and stay untranslated.
const getAnalysisStatusConfig = (t) => ({
    pending: { text: t("onboarding.analysisStatus.pending"), variant: "default", Icon: IconSquareRoundedMinus },
    rejected: { text: t("onboarding.analysisStatus.rejected"), variant: "error", Icon: IconAlertTriangle },
    completed: { text: t("onboarding.analysisStatus.completed"), variant: "success", Icon: IconChecks },
    approved: { text: t("onboarding.analysisStatus.approved"), variant: "success", Icon: IconChecks },
    active: { text: t("onboarding.analysisStatus.active"), variant: "success", Icon: IconChecks },
    awaiting_approval: { text: t("onboarding.analysisStatus.awaitingApproval"), variant: "info", Icon: IconRefresh },
});

function AnalysisStatusRow({ label, status }) {
    const { t } = useTranslation("banking");
    const statusConfig = useMemo(() => getAnalysisStatusConfig(t), [t]);

    const config = statusConfig[status] || statusConfig.pending;
    const { Icon } = config;
    return (
        <div className="analysis-status-row">
            <StatusChip
                variant={config.variant}
                text={config.text}
                icon={
                    <Icon
                        className={status === 'awaiting_approval' ? 'analysis-spin' : ''}
                        size={16}
                    />
                }
            />
            <Text.h5 contrast={7}>{label}</Text.h5>
        </div>
    );
}

export default function BankOnboardingView({
    // Form
    getName, setGetName,
    getPhone, setGetPhone,
    getAddress, setGetAddress,
    getLegalType, setGetLegalType,
    getDocument, setGetDocument,
    getBirthDate, setGetBirthDate,
    getCompanyType, setGetCompanyType,
    getIncome, setGetIncome,
    getPixKeyType, setGetPixKeyType,
    getPixKey, setGetPixKey,
    // API / status
    account,
    loadingWalletStatus,
    submitting,
    errors,
    creatingProgress,
    walletCreated,
    walletInProgress,
    // Accordion control
    currentStep, setCurrentStep,
    completedSteps,
    // Validators
    validateStep1,
    validateStep2,
    validateStep3,
    validateStep4,
    // Actions
    handleDocumentUpload,
    handleRefreshStatus,
}) {
    const { t } = useTranslation("banking");
    const documents = account?.provider?.pending_documents || [];
    const statuses = account?.statuses || {};

    const docsFinished = account?.id && (documents.length === 0 || documents.every(doc => doc.status?.toLowerCase() === 'approved'));

    const analysisCompleted =
        statuses.general === 'approved' || statuses.general === 'active';

    // Steps 4–5 unlock once the wallet exists or creation is underway
    const step4Disabled = (!walletCreated && !walletInProgress) || statuses.documentation === 'awaiting_approval';
    const step5Disabled = !walletCreated;

    return (
        <motion.div
            className="bank-onboarding"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
        >
            {/* ── Header ─────────────────────────────────────────────── */}
            <div className="bank-onboarding-header pt-3">
                <GinasteeCard className="bank-onboarding-card-img" />
                <div>
                    <Text.h2 weight={600}>Ginastee Wallet</Text.h2>
                    <Text.h5 contrast={5} weight={300} className="mt-1">
                        {t("onboarding.subtitle")}
                    </Text.h5>
                </div>
            </div>

            {/* ── Accordion group ────────────────────────────────────── */}
            <div className="accordion-group mt-6 mb-6">

                {/* Step 1 – personal info */}
                <Accordion
                    title={t("onboarding.steps.personal.title")}
                    stepTitle={t("onboarding.steps.personal.stepTitle")}
                    stepDescription={t("onboarding.steps.personal.stepDescription")}
                    summary={completedSteps[1] ? `${getName} • ${Parser.phone(getPhone)}` : null}
                    isOpen={!loadingWalletStatus && currentStep === 1}
                    isCompleted={completedSteps[1]}
                    onToggle={() => setCurrentStep(currentStep === 1 ? null : 1)}
                    onNext={validateStep1}
                    disabled={loadingWalletStatus || walletCreated || walletInProgress}
                    loading={loadingWalletStatus}
                >
                    <Input.FullName
                        placeholder={t("onboarding.steps.personal.namePlaceholder")}
                        value={getName}
                        onInput={setGetName}
                        autoFocus
                    />
                    <Input.Phone
                        value={getPhone}
                        onInput={setGetPhone}
                        label={t("onboarding.steps.personal.phoneLabel")}
                    />
                </Accordion>

                {/* Step 2 – address */}
                <Accordion
                    title={t("onboarding.steps.address.title")}
                    stepTitle={t("onboarding.steps.address.stepTitle")}
                    stepDescription={t("onboarding.steps.address.stepDescription")}
                    summary={completedSteps[2] && getAddress ? `${getAddress.street}, ${getAddress.number}${getAddress.complement ? ` - ${getAddress.complement}` : ''} - ${getAddress.neighborhood}, ${getAddress.city} - ${getAddress.state}, ${t("onboarding.steps.address.summaryZipcode", { zipcode: getAddress.zipcode?.replace(/^(\d{5})(\d{3})$/, "$1-$2") })}` : null}
                    isOpen={!loadingWalletStatus && currentStep === 2}
                    isCompleted={completedSteps[2]}
                    onToggle={() => setCurrentStep(currentStep === 2 ? null : 2)}
                    onNext={validateStep2}
                    disabled={loadingWalletStatus || !completedSteps[1] || walletCreated || walletInProgress}
                    loading={loadingWalletStatus}
                >
                    <Input.Address
                        value={getAddress}
                        onInput={setGetAddress}
                    />
                </Accordion>

                {/* Step 3 – accounting info */}
                <Accordion
                    title={t("onboarding.steps.accounting.title")}
                    stepTitle={t("onboarding.steps.accounting.stepTitle")}
                    stepDescription={t("onboarding.steps.accounting.stepDescription")}
                    summary={completedSteps[3] || walletCreated ? (getLegalType === 'physical' ? t("onboarding.steps.accounting.summaryIndividual", { document: Parser.cpf(getDocument), birthDate: Parser.date.ddmmyyyy(getBirthDate) }) : t("onboarding.steps.accounting.summaryCompany", { document: Parser.cnpj(getDocument) })) : null}
                    isOpen={!loadingWalletStatus && currentStep === 3}
                    isCompleted={completedSteps[3] || walletCreated}
                    onToggle={() => setCurrentStep(currentStep === 3 ? null : 3)}
                    onNext={validateStep3}
                    disabled={loadingWalletStatus || !completedSteps[2] || walletCreated || walletInProgress}
                    loading={loadingWalletStatus || submitting}
                >
                    <Input.Choices
                        label={t("onboarding.steps.accounting.legalTypeLabel")}
                        options={[
                            { value: 'physical', label: t("onboarding.steps.accounting.legalTypeIndividual") },
                            { value: 'juridical', label: t("onboarding.steps.accounting.legalTypeCompany") },
                        ]}
                        value={getLegalType}
                        onInput={setGetLegalType}
                    />

                    {getLegalType === 'physical' ? (
                        <>
                            <Input.CPF
                                value={getDocument}
                                onInput={setGetDocument}
                                label={t("common:fields.cpf")}
                            />
                            <Input.Date
                                label={t("onboarding.steps.accounting.birthDateLabel")}
                                value={getBirthDate}
                                onInput={setGetBirthDate}
                            />
                        </>
                    ) : (
                        <>
                            <Input.Select
                                label={t("onboarding.steps.accounting.companyTypeLabel")}
                                placeholder={t("onboarding.steps.accounting.companyTypePlaceholder")}
                                options={[
                                    { label: t("onboarding.companyTypes.mei"), value: "MEI" },
                                    { label: t("onboarding.companyTypes.limited"), value: "LIMITED" },
                                    { label: t("onboarding.companyTypes.individual"), value: "INDIVIDUAL" },
                                    { label: t("onboarding.companyTypes.association"), value: "ASSOCIATION" },
                                ]}
                                value={getCompanyType}
                                onInput={setGetCompanyType}
                            />
                            <Input.CNPJ
                                value={getDocument}
                                onInput={setGetDocument}
                                label={t("common:fields.cnpj")}
                            />
                        </>
                    )}

                    <Input.Money
                        value={getIncome}
                        onInput={setGetIncome}
                        label={getLegalType === 'physical' ? t("onboarding.steps.accounting.incomeIndividual") : t("onboarding.steps.accounting.incomeCompany")}
                    />

                    <Text.h4 className="mt-3" weight={400}>{t("onboarding.steps.accounting.pixTitle")}</Text.h4>

                    <Input.Select
                        label={t("onboarding.steps.accounting.pixKeyTypeLabel")}
                        placeholder={t("onboarding.steps.accounting.pixKeyTypePlaceholder")}
                        options={[
                            { label: t("onboarding.pixKeyTypes.cpf"), value: "CPF" },
                            { label: t("onboarding.pixKeyTypes.cnpj"), value: "CNPJ" },
                            { label: t("onboarding.pixKeyTypes.email"), value: "EMAIL" },
                            { label: t("onboarding.pixKeyTypes.phone"), value: "PHONE" },
                            { label: t("onboarding.pixKeyTypes.random"), value: "EVP" },
                        ]}
                        value={getPixKeyType}
                        onInput={(val) => {
                            setGetPixKeyType(val);
                            setGetPixKey('');
                        }}
                    />

                    {(() => {
                        const baseProps = {
                            value: getPixKey,
                            onInput: setGetPixKey,
                            label: t("onboarding.steps.accounting.pixKeyLabel"),
                        };

                        switch (getPixKeyType) {
                            case 'CPF':
                                return <Input.CPF {...baseProps} placeholder="000.000.000-00" />;
                            case 'CNPJ':
                                return <Input.CNPJ {...baseProps} placeholder="00.000.000/0000-00" />;
                            case 'EMAIL':
                                return <Input.Text {...baseProps} placeholder={t("onboarding.steps.accounting.pixEmailPlaceholder")} type="email" />;
                            case 'PHONE':
                                return <Input.Phone {...baseProps} />;
                            case 'EVP':
                            default:
                                return <Input.Text {...baseProps} placeholder={t("onboarding.steps.accounting.pixRandomPlaceholder")} />;
                        }
                    })()}

                    <Text.span contrast={5} left={<IconInfoCircle />}>
                        {t("onboarding.steps.accounting.incomeNotice")}
                    </Text.span>

                    {errors && (
                        <Mui.Alert severity="error" className="mt-2">
                            {errors}
                        </Mui.Alert>
                    )}
                </Accordion>

                {/* Step 4 – documents */}
                <Accordion
                    title={t("onboarding.steps.documents.title")}
                    stepTitle={t("onboarding.steps.documents.stepTitle")}
                    stepDescription={t("onboarding.steps.documents.stepDescription")}
                    isOpen={currentStep === 4}
                    isCompleted={docsFinished}
                    onToggle={() => setCurrentStep(currentStep === 4 ? null : 4)}
                    onNext={validateStep4}
                    disabled={step4Disabled || docsFinished || (!completedSteps[3] && !walletCreated)}
                    loading={loadingWalletStatus}
                >
                    {walletInProgress ? (
                        <div className="wallet-creating-state">
                            <Text.h5 contrast={6} className="mb-3">
                                {t("onboarding.steps.documents.creating")}
                            </Text.h5>
                            <Mui.LinearProgress
                                variant="determinate"
                                value={((18 - creatingProgress) / 18) * 100}
                                className="wallet-creating-progress-bar"
                            />
                            {creatingProgress > 0 && (
                                <Text.span contrast={5} className="mt-2">
                                    {t("onboarding.steps.documents.secondsRemaining", { count: creatingProgress })}
                                </Text.span>
                            )}
                        </div>
                    ) : (
                        <div className="documents-list">
                            {documents.length === 0 ? (
                                <Text.h5 contrast={5}>{t("onboarding.steps.documents.empty")}</Text.h5>
                            ) : (
                                documents.map((doc, i) => (
                                    <DocumentRow
                                        key={doc.id ?? i}
                                        document={doc}
                                        onUpload={handleDocumentUpload}
                                        onRefresh={handleRefreshStatus}
                                    />
                                ))
                            )}
                        </div>
                    )}
                </Accordion>

                {/* Step 5 – analysis (read-only, no onNext) */}
                <Accordion
                    title={t("onboarding.steps.analysis.title")}
                    stepTitle={t("onboarding.steps.analysis.stepTitle")}
                    stepDescription={t("onboarding.steps.analysis.stepDescription")}
                    isOpen={currentStep === 5}
                    isCompleted={analysisCompleted}
                    onToggle={() => setCurrentStep(currentStep === 5 ? null : 5)}
                    disabled={step5Disabled}
                    loading={loadingWalletStatus}
                >
                    <div className="analysis-status-list">
                        <AnalysisStatusRow
                            label={t("onboarding.steps.analysis.commercial")}
                            status={statuses.commercial}
                        />
                        <AnalysisStatusRow
                            label={t("onboarding.steps.analysis.documentation")}
                            status={statuses.documentation}
                        />
                        <AnalysisStatusRow
                            label={t("onboarding.steps.analysis.general")}
                            status={statuses.general}
                        />
                    </div>
                </Accordion>

            </div>
        </motion.div>
    );
}
